import { useState, useEffect, useCallback } from 'react';
import { Search, Loader2, AlertCircle, X, Package, Trash2, Check } from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PartSaleTracker = () => {
  const perms = detectPerms();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/part-sale-tracker`, getAuthHeaders());
      setItems(res.data);
    } catch { setError('Failed to fetch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/part-sale-tracker/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchItems();
    } catch { setError('Failed to delete'); }
  };

  const handleSaveItemId = async (trackerId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/part-sale-tracker/${trackerId}`, { item_id: editValue || null }, getAuthHeaders());
      setEditingItemId(null);
      fetchItems();
    } catch { setError('Failed to update Item ID'); }
  };

  const startEditing = (tracker) => {
    setEditingItemId(tracker.id);
    setEditValue(tracker.item_id || '');
  };

  const filtered = items.filter(i =>
    (i.product_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (i.product_code || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (i.item_id || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getProgressColor = (remaining, full) => {
    const pct = full > 0 ? (remaining / full) * 100 : 0;
    if (pct > 60) return 'bg-green-500';
    if (pct > 25) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div data-testid="part-sale-tracker-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Part Sale Tracker</h1>
          <p className="text-slate-600 mt-1">Track products sold in parts</p>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by product name, code or item ID..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Package className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No part sale products</h3>
            <p className="text-slate-500">Mark products as "Part Sale" in the Products page to track them here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Item ID</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Unit</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Full Qty</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Sold</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Remaining</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider min-w-[200px]">Usage</th>
                  <th className="px-6 py-4 text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const pct = item.full_quantity > 0 ? ((item.full_quantity - item.remaining_quantity) / item.full_quantity) * 100 : 0;
                  const isEditing = editingItemId === item.id;
                  return (
                    <tr key={item.id} data-testid={`tracker-row-${item.id}`}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">{item.product_name || '-'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600 font-mono">{item.product_code || '-'}</td>
                      <td className="px-6 py-4">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <input type="text" value={editValue} onChange={e => setEditValue(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveItemId(item.id); if (e.key === 'Escape') setEditingItemId(null); }}
                              autoFocus data-testid={`item-id-input-${item.id}`}
                              className="w-28 px-2 py-1 border border-orange-300 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
                            <button onClick={() => handleSaveItemId(item.id)} data-testid={`item-id-save-${item.id}`}
                              className="p-1 text-green-600 hover:text-green-700"><Check className="h-4 w-4" /></button>
                            <button onClick={() => setEditingItemId(null)}
                              className="p-1 text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        ) : (
                          <button onClick={() => startEditing(item)} data-testid={`item-id-btn-${item.id}`}
                            className={`px-2 py-1 rounded-sm text-sm border transition-colors ${
                              item.item_id
                                ? 'border-slate-200 text-slate-900 font-mono hover:border-orange-300'
                                : 'border-dashed border-slate-300 text-slate-400 hover:border-orange-400 hover:text-orange-500'
                            }`}>
                            {item.item_id || 'Set Item ID'}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{item.unit_name || '-'}</td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{item.full_quantity}</td>
                      <td className="px-6 py-4 text-sm font-medium text-red-600">{item.sold_quantity}</td>
                      <td className="px-6 py-4 text-sm font-bold text-green-700">{item.remaining_quantity}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${getProgressColor(item.remaining_quantity, item.full_quantity)}`}
                              style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-slate-500 w-10 text-right">{pct.toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {perms.canDelete && (
                        <button onClick={() => setDeleteConfirm(item.id)} data-testid={`delete-btn-${item.id}`}
                          className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Remove this product from part sale tracking?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
